import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useCommonTranslation } from '../../modules/i18n';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'danger' | 'primary';
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  confirmVariant = 'danger',
  onConfirm,
  onCancel,
  isLoading = false
}) => {
  const { t } = useCommonTranslation();
  const [isRendered, setIsRendered] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      // Đợi 10ms để component được render trước khi hiển thị
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
      // Đợi animation kết thúc trước khi xóa component khỏi DOM
      setTimeout(() => setIsRendered(false), 300);
    }
  }, [isOpen]);

  if (!isRendered) return null;

  const confirmButtonClass =
    confirmVariant === 'danger'
      ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
      : 'bg-primary hover:bg-primary-dark focus:ring-primary';

  const dialogContent = (
    <div
      className={`fixed inset-0 z-[200000] flex items-center justify-center transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      aria-labelledby='modal-title'
      role='dialog'
      aria-modal='true'
    >
      <div className='fixed inset-0 bg-gray-500/75 dark:bg-black/60 transition-opacity'></div>

      <div
        className={`relative mx-auto w-full max-w-md transform rounded-lg bg-white dark:bg-dark-background-secondary px-6 py-5 shadow-xl transition-all duration-300 ${
          isVisible ? 'translate-y-0' : 'translate-y-4'
        }`}
      >
        <div className='flex flex-col'>
          <div className='flex'>
            <div className='flex-shrink-0'>
              <ExclamationTriangleIcon className='h-6 w-6 text-yellow-500' aria-hidden='true' />
            </div>
            <h3 className='ml-3 line-clamp-1 text-lg font-medium text-gray-900 dark:text-dark-text' id='modal-title'>
              {title}
            </h3>
          </div>

          {message && (
            <div className='m-2'>
              <p className='line-clamp-2 text-sm text-gray-500 dark:text-dark-text-secondary'>{message}</p>
            </div>
          )}
        </div>
        <div className='flex justify-end space-x-3'>
          <button
            type='button'
            className='inline-flex justify-center rounded-md border border-gray-300 dark:border-border-dark bg-white dark:bg-dark-background-tertiary px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-text shadow-sm hover:bg-gray-50 dark:hover:bg-dark-background-hover focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed'
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancelText || t('actions.cancel')}
          </button>
          <button
            type='button'
            className={`inline-flex justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm ${confirmButtonClass} focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {isLoading ? t('status.processing') : (confirmText || t('actions.confirm'))}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(dialogContent, document.body);
};
